// ═══════════════════════════════════════════════════════════════
// PIXALLOY — Vector File Import (SVG, EPS)
// ═══════════════════════════════════════════════════════════════
// Parses SVG and EPS vector files into PathList data suitable
// for creating PixAlloy vector layers.
//
// SVG: full DOM parsing of <path>, <rect>, <circle>, <ellipse>,
//      <polygon>, <polyline>, <line> elements with style extraction.
// EPS: simplified PostScript subset parsing (moveto, lineto,
//      curveto, closepath) for basic vector content.
// ═══════════════════════════════════════════════════════════════

import { mkPt, mkPath } from "./penTool.js";

// ── SVG path "d" attribute parser ────────────────────────────
// Converts SVG path data to a PathList-compatible point array.
function parseSvgPathD(d) {
  if (!d) return [];
  const paths = [];
  let current = [];
  let cx = 0, cy = 0, startX = 0, startY = 0;
  let lastCp2x = null, lastCp2y = null;

  // Tokenize: split into commands + numbers
  const tokens = d.match(/[a-zA-Z]|[-+]?(?:\d+\.?\d*|\.\d+)(?:[eE][-+]?\d+)?/g);
  if (!tokens) return [];

  let i = 0;
  const num = () => parseFloat(tokens[i++]);
  const hasNums = () => i < tokens.length && /[-+.\d]/.test(tokens[i]);

  while (i < tokens.length) {
    const cmd = tokens[i];
    if (/[a-zA-Z]/.test(cmd)) {
      i++;
    } else {
      break; // safety
    }

    switch (cmd) {
      case "M": while (hasNums()) { cx = num(); cy = num(); startX = cx; startY = cy; if (current.length > 0) { paths.push(current); current = []; } current.push(mkPt(cx, cy)); } break;
      case "m": while (hasNums()) { cx += num(); cy += num(); startX = cx; startY = cy; if (current.length > 0) { paths.push(current); current = []; } current.push(mkPt(cx, cy)); } break;

      case "L": while (hasNums()) { cx = num(); cy = num(); current.push(mkPt(cx, cy)); } break;
      case "l": while (hasNums()) { cx += num(); cy += num(); current.push(mkPt(cx, cy)); } break;

      case "H": while (hasNums()) { cx = num(); current.push(mkPt(cx, cy)); } break;
      case "h": while (hasNums()) { cx += num(); current.push(mkPt(cx, cy)); } break;

      case "V": while (hasNums()) { cy = num(); current.push(mkPt(cx, cy)); } break;
      case "v": while (hasNums()) { cy += num(); current.push(mkPt(cx, cy)); } break;

      case "C": while (hasNums()) {
        const c1x = num(), c1y = num(), c2x = num(), c2y = num(), x = num(), y = num();
        if (current.length > 0) { const last = current[current.length - 1]; last.cp2x = c1x; last.cp2y = c1y; }
        current.push(mkPt(x, y, c2x, c2y));
        cx = x; cy = y; lastCp2x = c2x; lastCp2y = c2y;
      } break;
      case "c": while (hasNums()) {
        const c1x = cx + num(), c1y = cy + num(), c2x = cx + num(), c2y = cy + num();
        const x = cx + num(), y = cy + num();
        if (current.length > 0) { const last = current[current.length - 1]; last.cp2x = c1x; last.cp2y = c1y; }
        current.push(mkPt(x, y, c2x, c2y));
        cx = x; cy = y; lastCp2x = c2x; lastCp2y = c2y;
      } break;

      case "S": while (hasNums()) {
        // Smooth cubic: reflect last cp2 for cp1
        let c1x = cx, c1y = cy;
        if (lastCp2x != null) { c1x = 2 * cx - lastCp2x; c1y = 2 * cy - lastCp2y; }
        const c2x = num(), c2y = num(), x = num(), y = num();
        if (current.length > 0) { const last = current[current.length - 1]; last.cp2x = c1x; last.cp2y = c1y; }
        current.push(mkPt(x, y, c2x, c2y));
        cx = x; cy = y; lastCp2x = c2x; lastCp2y = c2y;
      } break;
      case "s": while (hasNums()) {
        let c1x = cx, c1y = cy;
        if (lastCp2x != null) { c1x = 2 * cx - lastCp2x; c1y = 2 * cy - lastCp2y; }
        const c2x = cx + num(), c2y = cy + num(), x = cx + num(), y = cy + num();
        if (current.length > 0) { const last = current[current.length - 1]; last.cp2x = c1x; last.cp2y = c1y; }
        current.push(mkPt(x, y, c2x, c2y));
        cx = x; cy = y; lastCp2x = c2x; lastCp2y = c2y;
      } break;

      case "Q": while (hasNums()) {
        // Quadratic → approximate with cubic control points
        const qx = num(), qy = num(), x = num(), y = num();
        const c1x = cx + 2 / 3 * (qx - cx), c1y = cy + 2 / 3 * (qy - cy);
        const c2x = x + 2 / 3 * (qx - x), c2y = y + 2 / 3 * (qy - y);
        if (current.length > 0) { const last = current[current.length - 1]; last.cp2x = c1x; last.cp2y = c1y; }
        current.push(mkPt(x, y, c2x, c2y));
        cx = x; cy = y; lastCp2x = null; lastCp2y = null;
      } break;
      case "q": while (hasNums()) {
        const qx = cx + num(), qy = cy + num(), x = cx + num(), y = cy + num();
        const c1x = cx + 2 / 3 * (qx - cx), c1y = cy + 2 / 3 * (qy - cy);
        const c2x = x + 2 / 3 * (qx - x), c2y = y + 2 / 3 * (qy - y);
        if (current.length > 0) { const last = current[current.length - 1]; last.cp2x = c1x; last.cp2y = c1y; }
        current.push(mkPt(x, y, c2x, c2y));
        cx = x; cy = y; lastCp2x = null; lastCp2y = null;
      } break;

      case "A": case "a": {
        // Arc: approximate as line to endpoint (full arc conversion is very complex)
        while (hasNums()) {
          num(); num(); num(); num(); num(); // rx, ry, rotation, large-arc, sweep
          const ax = cmd === "A" ? num() : cx + num();
          const ay = cmd === "A" ? num() : cy + num();
          current.push(mkPt(ax, ay));
          cx = ax; cy = ay;
        }
      } break;

      case "Z": case "z":
        if (current.length >= 2) { current.push(mkPt(startX, startY)); }
        cx = startX; cy = startY;
        lastCp2x = null; lastCp2y = null;
        break;

      default:
        // Unknown command, skip
        break;
    }

    // Reset smooth reflection for non-curve commands
    if (cmd !== "C" && cmd !== "c" && cmd !== "S" && cmd !== "s") {
      lastCp2x = null; lastCp2y = null;
    }
  }

  if (current.length > 0) paths.push(current);
  return paths;
}

// ── Extract style from SVG element attributes + inline style ─
function extractSvgStyle(el) {
  const style = {};
  const get = (attr) => el.getAttribute(attr);
  const inl = get("style") || "";

  // Parse inline style
  const inlMap = {};
  inl.split(";").forEach(s => {
    const [k, v] = s.split(":").map(x => x?.trim());
    if (k && v) inlMap[k] = v;
  });

  const resolve = (attr) => inlMap[attr] || get(attr);

  const fill = resolve("fill");
  if (fill && fill !== "none") style.fillColor = fill;
  else if (fill === "none") style.fillColor = null;

  const stroke = resolve("stroke");
  if (stroke && stroke !== "none") style.strokeColor = stroke;

  const sw = resolve("stroke-width");
  if (sw) style.strokeWidth = parseFloat(sw) || 2;

  const op = resolve("opacity") || resolve("fill-opacity");
  if (op) style.opacity = parseFloat(op);

  return style;
}

// ── Convert SVG shape elements to PathList ───────────────────

function rectToPoints(x, y, w, h, rx, ry) {
  rx = rx || 0; ry = ry || rx;
  if (rx === 0 && ry === 0) {
    return [mkPt(x, y), mkPt(x + w, y), mkPt(x + w, y + h), mkPt(x, y + h), mkPt(x, y)];
  }
  // Rounded rect approximation with bezier arcs
  const k = 0.5522847498;
  const pts = [];
  pts.push(mkPt(x + rx, y));
  pts.push(mkPt(x + w - rx, y, null, null, x + w - rx + rx * k, y));
  pts.push(mkPt(x + w, y + ry, x + w, y + ry - ry * k, null, null));
  pts.push(mkPt(x + w, y + h - ry, null, null, x + w, y + h - ry + ry * k));
  pts.push(mkPt(x + w - rx, y + h, x + w - rx + rx * k, y + h, null, null));
  pts.push(mkPt(x + rx, y + h, null, null, x + rx - rx * k, y + h));
  pts.push(mkPt(x, y + h - ry, x, y + h - ry + ry * k, null, null));
  pts.push(mkPt(x, y + ry, null, null, x, y + ry - ry * k));
  pts.push(mkPt(x + rx, y, x + rx - rx * k, y, null, null));
  return pts;
}

function circleToPoints(cx, cy, r) {
  return ellipseToPoints(cx, cy, r, r);
}

function ellipseToPoints(cx, cy, rx, ry) {
  const k = 0.5522847498;
  return [
    { x: cx, y: cy - ry, cp1x: cx + rx * k, cp1y: cy - ry, cp2x: cx + rx * k, cp2y: cy - ry },
    { x: cx + rx, y: cy, cp1x: cx + rx, cp1y: cy - ry * k, cp2x: cx + rx, cp2y: cy + ry * k },
    { x: cx, y: cy + ry, cp1x: cx + rx * k, cp1y: cy + ry, cp2x: cx - rx * k, cp2y: cy + ry },
    { x: cx - rx, y: cy, cp1x: cx - rx, cp1y: cy + ry * k, cp2x: cx - rx, cp2y: cy - ry * k },
    { x: cx, y: cy - ry, cp1x: cx - rx * k, cp1y: cy - ry, cp2x: null, cp2y: null },
  ];
}

function polygonToPoints(pointsAttr) {
  const nums = (pointsAttr || "").trim().split(/[\s,]+/).map(Number);
  const pts = [];
  for (let i = 0; i < nums.length - 1; i += 2) {
    pts.push(mkPt(nums[i], nums[i + 1]));
  }
  if (pts.length >= 2) pts.push(mkPt(pts[0].x, pts[0].y));
  return pts;
}

function lineToPoints(x1, y1, x2, y2) {
  return [mkPt(x1, y1), mkPt(x2, y2)];
}

// ── Apply SVG transform attribute ────────────────────────────
function applyTransformToPoints(pts, transformAttr) {
  if (!transformAttr) return pts;
  // Parse transform="translate(x,y) scale(sx,sy) rotate(deg)" etc.
  const m = { a: 1, b: 0, c: 0, d: 1, e: 0, f: 0 }; // identity matrix

  const ops = transformAttr.match(/\w+\([^)]+\)/g) || [];
  for (const op of ops) {
    const name = op.match(/^\w+/)[0];
    const args = op.match(/[-+]?[\d.]+/g)?.map(Number) || [];
    if (name === "translate") { m.e += args[0] || 0; m.f += args[1] || 0; }
    else if (name === "scale") {
      const sx = args[0] || 1, sy = args[1] ?? sx;
      m.a *= sx; m.d *= sy; m.e *= sx; m.f *= sy;
    }
    else if (name === "rotate") {
      const deg = args[0] || 0, rad = deg * Math.PI / 180;
      const cos = Math.cos(rad), sin = Math.sin(rad);
      const na = m.a * cos + m.c * sin, nb = m.b * cos + m.d * sin;
      const nc = m.a * -sin + m.c * cos, nd = m.b * -sin + m.d * cos;
      m.a = na; m.b = nb; m.c = nc; m.d = nd;
    }
    else if (name === "matrix") {
      // matrix(a,b,c,d,e,f)
      if (args.length >= 6) {
        m.a = args[0]; m.b = args[1]; m.c = args[2]; m.d = args[3]; m.e = args[4]; m.f = args[5];
      }
    }
  }

  return pts.map(pt => {
    const r = { ...pt };
    r.x = m.a * pt.x + m.c * pt.y + m.e;
    r.y = m.b * pt.x + m.d * pt.y + m.f;
    if (pt.cp1x != null) { r.cp1x = m.a * pt.cp1x + m.c * pt.cp1y + m.e; r.cp1y = m.b * pt.cp1x + m.d * pt.cp1y + m.f; }
    if (pt.cp2x != null) { r.cp2x = m.a * pt.cp2x + m.c * pt.cp2y + m.e; r.cp2y = m.b * pt.cp2x + m.d * pt.cp2y + m.f; }
    return r;
  });
}

// ═══════════════════════════════════════════════════════════════
// SVG FILE IMPORT
// ═══════════════════════════════════════════════════════════════

/**
 * Parse an SVG string into an array of { paths: Path[], style, name }.
 * Each shape element becomes a separate entry.
 * Returns { layers: [{name, paths: Path[], style}], viewBox: {w,h} }
 */
export function parseSvg(svgString) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(svgString, "image/svg+xml");
  const svg = doc.querySelector("svg");
  if (!svg) return { layers: [], viewBox: { w: 100, h: 100 } };

  // Get viewBox dimensions
  const vb = svg.getAttribute("viewBox");
  let vbW = parseFloat(svg.getAttribute("width")) || 100;
  let vbH = parseFloat(svg.getAttribute("height")) || 100;
  if (vb) {
    const parts = vb.split(/[\s,]+/).map(Number);
    if (parts.length >= 4) { vbW = parts[2]; vbH = parts[3]; }
  }

  const layers = [];
  const SHAPE_SELECTOR = "path, rect, circle, ellipse, polygon, polyline, line";

  // Walk all shape elements (including nested in <g>)
  svg.querySelectorAll(SHAPE_SELECTOR).forEach((el, idx) => {
    let points = [];
    const tag = el.tagName.toLowerCase();
    const tf = el.getAttribute("transform");
    // Also accumulate parent <g> transforms
    let parentTf = "";
    let parent = el.parentElement;
    while (parent && parent !== svg) {
      if (parent.getAttribute("transform")) parentTf = parent.getAttribute("transform") + " " + parentTf;
      parent = parent.parentElement;
    }
    const fullTf = (parentTf + " " + (tf || "")).trim() || null;

    if (tag === "path") {
      const pathArrays = parseSvgPathD(el.getAttribute("d"));
      for (const pts of pathArrays) {
        let transformed = fullTf ? applyTransformToPoints(pts, fullTf) : pts;
        const closed = pts.length >= 2 && pts[pts.length - 1].x === pts[0].x && pts[pts.length - 1].y === pts[0].y;
        layers.push({
          name: el.getAttribute("id") || `path-${idx}`,
          paths: [mkPath(transformed, closed, el.getAttribute("id"))],
          style: extractSvgStyle(el),
        });
      }
      return;
    }

    if (tag === "rect") {
      points = rectToPoints(
        parseFloat(el.getAttribute("x") || 0), parseFloat(el.getAttribute("y") || 0),
        parseFloat(el.getAttribute("width") || 0), parseFloat(el.getAttribute("height") || 0),
        parseFloat(el.getAttribute("rx") || 0), parseFloat(el.getAttribute("ry") || 0)
      );
    } else if (tag === "circle") {
      points = circleToPoints(
        parseFloat(el.getAttribute("cx") || 0), parseFloat(el.getAttribute("cy") || 0),
        parseFloat(el.getAttribute("r") || 0)
      );
    } else if (tag === "ellipse") {
      points = ellipseToPoints(
        parseFloat(el.getAttribute("cx") || 0), parseFloat(el.getAttribute("cy") || 0),
        parseFloat(el.getAttribute("rx") || 0), parseFloat(el.getAttribute("ry") || 0)
      );
    } else if (tag === "polygon" || tag === "polyline") {
      points = polygonToPoints(el.getAttribute("points"));
      if (tag === "polyline" && points.length > 0) points.pop(); // polyline is open
    } else if (tag === "line") {
      points = lineToPoints(
        parseFloat(el.getAttribute("x1") || 0), parseFloat(el.getAttribute("y1") || 0),
        parseFloat(el.getAttribute("x2") || 0), parseFloat(el.getAttribute("y2") || 0)
      );
    }

    if (points.length >= 2) {
      if (fullTf) points = applyTransformToPoints(points, fullTf);
      const closed = points.length >= 3 && points[points.length - 1].x === points[0].x && points[points.length - 1].y === points[0].y;
      layers.push({
        name: el.getAttribute("id") || `${tag}-${idx}`,
        paths: [mkPath(points, closed, el.getAttribute("id") || tag)],
        style: extractSvgStyle(el),
      });
    }
  });

  return { layers, viewBox: { w: vbW, h: vbH } };
}

// ═══════════════════════════════════════════════════════════════
// EPS FILE IMPORT (simplified PostScript subset)
// ═══════════════════════════════════════════════════════════════

/**
 * Parse a simplified EPS file into vector paths.
 * Handles: moveto (m), lineto (l), curveto (c), closepath,
 *          setrgbcolor, setlinewidth, stroke, fill, gsave/grestore.
 *
 * Many EPS files won't parse fully — this handles the common
 * Illustrator/Inkscape export subset.
 * Returns { layers: [{name, paths: Path[], style}], bbox: {x,y,w,h} }
 */
export function parseEps(epsString) {
  const layers = [];
  let currentPts = [];
  let allSubpaths = [];
  let cx = 0, cy = 0, startX = 0, startY = 0;
  let strokeColor = "#000000";
  let fillColor = null;
  let lineWidth = 1;
  let pathIdx = 0;

  // Extract BoundingBox
  const bbMatch = epsString.match(/%%BoundingBox:\s*([-\d.]+)\s+([-\d.]+)\s+([-\d.]+)\s+([-\d.]+)/);
  const bbox = bbMatch ? {
    x: parseFloat(bbMatch[1]), y: parseFloat(bbMatch[2]),
    w: parseFloat(bbMatch[3]) - parseFloat(bbMatch[1]),
    h: parseFloat(bbMatch[4]) - parseFloat(bbMatch[2]),
  } : { x: 0, y: 0, w: 612, h: 792 };

  // EPS uses bottom-left origin; flip Y relative to bbox
  const flipY = (y) => bbox.y + bbox.h - (y - bbox.y);

  const finishPath = (doStroke, doFill) => {
    if (currentPts.length > 0) allSubpaths.push(currentPts);
    if (allSubpaths.length === 0) return;
    const paths = allSubpaths.map(pts => {
      const closed = pts.length >= 3 && Math.abs(pts[pts.length - 1].x - pts[0].x) < 0.5 && Math.abs(pts[pts.length - 1].y - pts[0].y) < 0.5;
      return mkPath(pts, closed, `eps-${pathIdx++}`);
    });
    const style = {};
    if (doStroke) { style.strokeColor = strokeColor; style.strokeWidth = lineWidth; }
    if (doFill) { style.fillColor = fillColor || strokeColor; }
    layers.push({ name: `Shape ${layers.length + 1}`, paths, style });
    currentPts = [];
    allSubpaths = [];
  };

  // Tokenize: split on whitespace, ignoring comments (%)
  const lines = epsString.split("\n");
  const stack = [];

  for (const rawLine of lines) {
    const line = rawLine.replace(/%.*$/, "").trim();
    if (!line) continue;

    const tokens = line.split(/\s+/);
    for (const tok of tokens) {
      const n = parseFloat(tok);
      if (!isNaN(n) && isFinite(n)) { stack.push(n); continue; }

      switch (tok) {
        case "moveto": case "m": {
          const y = flipY(stack.pop()), x = stack.pop();
          if (currentPts.length > 0) { allSubpaths.push(currentPts); currentPts = []; }
          cx = x; cy = y; startX = x; startY = y;
          currentPts.push(mkPt(x, y));
        } break;

        case "lineto": case "l": {
          const y = flipY(stack.pop()), x = stack.pop();
          currentPts.push(mkPt(x, y));
          cx = x; cy = y;
        } break;

        case "curveto": case "c": {
          const y3 = flipY(stack.pop()), x3 = stack.pop();
          const y2 = flipY(stack.pop()), x2 = stack.pop();
          const y1 = flipY(stack.pop()), x1 = stack.pop();
          if (currentPts.length > 0) { const last = currentPts[currentPts.length - 1]; last.cp2x = x1; last.cp2y = y1; }
          currentPts.push(mkPt(x3, y3, x2, y2));
          cx = x3; cy = y3;
        } break;

        case "closepath": case "h":
          if (currentPts.length >= 2) currentPts.push(mkPt(startX, startY));
          cx = startX; cy = startY;
          break;

        case "stroke": finishPath(true, false); break;
        case "fill": case "eofill": finishPath(false, true); break;

        case "setrgbcolor": {
          const b = Math.round((stack.pop() || 0) * 255);
          const g = Math.round((stack.pop() || 0) * 255);
          const r = Math.round((stack.pop() || 0) * 255);
          strokeColor = `#${r.toString(16).padStart(2, "0")}${g.toString(16).padStart(2, "0")}${b.toString(16).padStart(2, "0")}`;
          fillColor = strokeColor;
        } break;

        case "setlinewidth": lineWidth = stack.pop() || 1; break;

        case "newpath": currentPts = []; allSubpaths = []; break;

        case "gsave": case "grestore": break; // ignore for now

        default: break; // ignore unknown operators
      }
    }
  }

  // Flush any remaining path
  finishPath(true, false);

  return { layers, bbox };
}

// ═══════════════════════════════════════════════════════════════
// SVG EXPORT from PathList
// ═══════════════════════════════════════════════════════════════

/**
 * Convert a PathList + style to an SVG string.
 */
export function pathListToSvg(pathList, style, w, h) {
  const paths = (pathList || []).map(path => {
    if (!path.points || path.points.length < 2) return "";
    const pts = path.points;
    let d = `M ${pts[0].x.toFixed(2)} ${pts[0].y.toFixed(2)}`;
    for (let i = 1; i < pts.length; i++) {
      const prev = pts[i - 1], cur = pts[i];
      const c1x = prev.cp2x != null ? prev.cp2x : prev.x;
      const c1y = prev.cp2y != null ? prev.cp2y : prev.y;
      const c2x = cur.cp1x != null ? cur.cp1x : cur.x;
      const c2y = cur.cp1y != null ? cur.cp1y : cur.y;
      if (c1x === prev.x && c1y === prev.y && c2x === cur.x && c2y === cur.y) {
        d += ` L ${cur.x.toFixed(2)} ${cur.y.toFixed(2)}`;
      } else {
        d += ` C ${c1x.toFixed(2)} ${c1y.toFixed(2)} ${c2x.toFixed(2)} ${c2y.toFixed(2)} ${cur.x.toFixed(2)} ${cur.y.toFixed(2)}`;
      }
    }
    if (path.closed) d += " Z";
    return d;
  }).filter(Boolean);

  const s = style || {};
  const fill = s.fillColor || "none";
  const stroke = s.strokeColor || "#000";
  const sw = s.strokeWidth || 2;

  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${w || 100} ${h || 100}" width="${w || 100}" height="${h || 100}">
${paths.map(d => `  <path d="${d}" fill="${fill}" stroke="${stroke}" stroke-width="${sw}" stroke-linecap="round" stroke-linejoin="round"/>`).join("\n")}
</svg>`;
}

// ═══════════════════════════════════════════════════════════════
// FILE TYPE DETECTION + UNIFIED IMPORT
// ═══════════════════════════════════════════════════════════════

/**
 * Detect file type and parse accordingly.
 * @param {string} content - File content as text
 * @param {string} filename - Original filename (for extension detection)
 * @returns {{ layers, viewBox?, bbox? }} or null if unrecognized
 */
export function parseVectorFile(content, filename) {
  const ext = (filename || "").toLowerCase().split(".").pop();
  const trimmed = content.trim();

  // SVG detection: starts with < and contains <svg
  if (ext === "svg" || (trimmed.startsWith("<") && trimmed.includes("<svg"))) {
    return parseSvg(content);
  }

  // EPS detection: starts with %!PS or has %%BoundingBox
  if (ext === "eps" || ext === "ai" || trimmed.startsWith("%!PS") || trimmed.includes("%%BoundingBox")) {
    return parseEps(content);
  }

  return null;
}
